/**
 * Styled MUI primitives for GKVNew module.
 *
 * These replace the scattered CSS classes (.box, .rx-box, .table-row, .table-header-cell,
 * .therapy-tag, .table-container, .bill-card) with composable styled components.
 *
 * Usage: Import these in GKVNew components instead of using className props.
 */
import { styled } from "@mui/material/styles";
import {
  Box,
  Card,
  CardContent,
  Paper,
  Table,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import { colors, fontSize, radius, shadow, spacing, table, card } from "./tokens";

// ═══════════════════════════════════════════════════════════════
// CARD PRIMITIVES
// ═══════════════════════════════════════════════════════════════

/** Compact card with tighter padding and subtle border */
export const CompactCard = styled(Card)({
  borderRadius: radius.lg,
  border: `1px solid ${colors.borderMedium}`,
  boxShadow: shadow.sm,
  overflow: "hidden",
});

/** Card content with reduced padding */
export const CompactCardContent = styled(CardContent)({
  padding: card.bodyPadding,
  "&:last-child": {
    paddingBottom: card.bodyPadding,
  },
});

/** Card header row — flex with space-between, compact vertical padding */
export const CardHeaderRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: card.headerPadding,
  borderBottom: `1px solid ${colors.borderLight}`,
  backgroundColor: colors.cardBg,
  borderRadius: `${radius.lg} ${radius.lg} 0 0`,
});

/** Horizontal metadata bar below card header */
export const MetaRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: spacing.lg,
  padding: `${spacing.sm} ${spacing.lg}`,
  fontSize: fontSize.data,
  color: colors.textSecondary,
});

/** Single key-value pair in the metadata row */
export const MetaItem = styled(Box)({
  display: "inline-flex",
  alignItems: "center",
  gap: spacing.sm,
  whiteSpace: "nowrap",
});

/** Label portion of a key-value pair */
export const MetaLabel = styled("span")({
  fontSize: fontSize.label,
  color: colors.textMuted,
  fontWeight: 500,
  textTransform: "uppercase",
  letterSpacing: "0.3px",
});

/** Value portion of a key-value pair */
export const MetaValue = styled("span")({
  fontSize: fontSize.data,
  color: colors.textPrimary,
  fontWeight: 500,
});

// ═══════════════════════════════════════════════════════════════
// TABLE PRIMITIVES
// ═══════════════════════════════════════════════════════════════

/** Table container with constrained height and subtle border */
export const DenseTableContainer = styled(TableContainer)({
  borderRadius: radius.md,
  border: `1px solid ${colors.borderLight}`,
  boxShadow: shadow.none,
  maxHeight: "350px",
  backgroundColor: colors.tableBg,
  overflow: "auto",
  "& .MuiTable-root": {
    borderRadius: radius.md,
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: colors.borderMedium,
    borderRadius: "6px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor: colors.borderDark,
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: colors.infoBg,
  },
  scrollbarWidth: "thin",
  scrollbarColor: `${colors.borderMedium} ${colors.infoBg}`,
});

/** Table with reduced spacing */
export const DenseTable = styled(Table)({
  tableLayout: "auto",
});

/** Header cell — compact, bold, no heavy gradient */
export const HeaderCell = styled(TableCell)({
  padding: table.headerPadding,
  fontSize: table.headerFontSize,
  fontWeight: 600,
  color: colors.textPrimary,
  backgroundColor: colors.tableBg,
  borderBottom: `1.5px solid ${colors.borderMedium}`,
  whiteSpace: "nowrap",
  position: "sticky",
  top: 0,
  zIndex: 2,
});

/** Data cell — compact padding, readable font size */
export const DataCell = styled(TableCell)({
  padding: table.cellPadding,
  fontSize: table.cellFontSize,
  color: colors.textPrimary,
  verticalAlign: "middle",
  borderBottom: `1px solid ${colors.borderLight}`,
});

/** Table row with clean hover (no scale transform) */
export const DenseTableRow = styled(TableRow, {
  shouldForwardProp: (prop) => prop !== "isSelected",
})<{ isSelected?: boolean }>(({ isSelected }) => ({
  cursor: "pointer",
  backgroundColor: isSelected ? colors.rowSelected : colors.infoBg,
  color: colors.textPrimary,
  transition: "background-color 0.15s ease",
  "&:hover": {
    backgroundColor: isSelected ? colors.rowSelected : colors.rowHover,
  },
}));

// ═══════════════════════════════════════════════════════════════
// DATA DISPLAY PRIMITIVES
// ═══════════════════════════════════════════════════════════════

/** Inline Rx details container — replaces .rx-box (no fixed width, no border-box) */
export const RxDetailsRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: spacing.md,
  fontSize: fontSize.data,
});

/** Therapy tag badge — compact inline chip */
export const TherapyTag = styled("span")({
  display: "inline-block",
  background: `linear-gradient(135deg, ${colors.therapyTag}, ${colors.therapyTagGradientEnd})`,
  color: colors.textOnDark,
  padding: "1px 5px",
  borderRadius: radius.sm,
  fontSize: fontSize.tag,
  fontWeight: 500,
  lineHeight: 1.4,
  marginLeft: spacing.xs,
});

// ═══════════════════════════════════════════════════════════════
// SUMMARY BAR PRIMITIVES
// ═══════════════════════════════════════════════════════════════

/** Container for summary stat items — horizontal row */
export const SummaryBar = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: spacing.sm,
  padding: `${spacing.md} 0`,
  flexWrap: "wrap",
});

/** Single summary stat item */
export const SummaryItem = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: spacing.sm,
  padding: `${spacing.sm} ${spacing.lg}`,
  minWidth: "fit-content",
});

/** Colored dot indicator for summary items */
export const StatusDot = styled("span", {
  shouldForwardProp: (prop) => prop !== "color",
})<{ color: string }>(({ color }) => ({
  width: "10px",
  height: "10px",
  borderRadius: "50%",
  backgroundColor: color,
  flexShrink: 0,
}));

/** Summary stat value */
export const SummaryValue = styled(Typography)({
  fontSize: fontSize.heading,
  fontWeight: 700,
  lineHeight: 1,
  color: colors.textOnDark,
});

/** Summary stat label */
export const SummaryLabel = styled(Typography)({
  fontSize: fontSize.label,
  color: "rgba(255, 255, 255, 0.7)",
  lineHeight: 1,
  marginLeft: spacing.xs,
});

// ═══════════════════════════════════════════════════════════════
// HEADER PRIMITIVES
// ═══════════════════════════════════════════════════════════════

/** Sticky header container */
export const StickyHeader = styled(Box)({
  position: "sticky",
  top: 0,
  zIndex: 1000,
  backgroundColor: colors.headerBg,
  borderRadius: `0 0 ${radius.lg} ${radius.lg}`,
  padding: spacing.md,
});

/** Section title — used in headers */
export const SectionTitle = styled(Typography)({
  fontSize: fontSize.headingLg,
  fontWeight: 700,
  color: colors.textOnDark,
  textAlign: "center",
});

// ═══════════════════════════════════════════════════════════════
// UTILITY PRIMITIVES
// ═══════════════════════════════════════════════════════════════

/** Empty state container */
export const EmptyState = styled(Box)({
  textAlign: "center",
  padding: `${spacing.xl} 0`,
  color: colors.textMuted,
  fontSize: fontSize.data,
});

/** Loading container */
export const LoadingContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: spacing.md,
  padding: `${spacing.xl} 0`,
});
